import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./DisclosureMotion.css";

export const DISCLOSURE_MOTION_MS = 190;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function DisclosureMotion({
  expanded,
  children,
  contentClassName = "",
  role,
  ariaLabel,
  allowOverflowWhenOpen = false,
  testId,
}) {
  const [isRendered, setIsRendered] = useState(expanded);
  const [isVisible, setIsVisible] = useState(expanded);
  const [isSettled, setIsSettled] = useState(expanded);
  const closeTimerRef = useRef(null);
  const openFrameRef = useRef(null);
  const settleTimerRef = useRef(null);
  const previousExpandedRef = useRef(expanded);

  useLayoutEffect(() => {
    const wasExpanded = previousExpandedRef.current;
    previousExpandedRef.current = expanded;

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (expanded) {
      setIsRendered(true);
      if (!wasExpanded) setIsSettled(false);
      return undefined;
    }

    setIsSettled(false);
    setIsVisible(false);
    const delay = prefersReducedMotion() ? 0 : DISCLOSURE_MOTION_MS;
    closeTimerRef.current = setTimeout(() => {
      setIsRendered(false);
      closeTimerRef.current = null;
    }, delay);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [expanded]);

  useLayoutEffect(() => {
    if (!isRendered || !expanded || isVisible) return undefined;

    openFrameRef.current = requestAnimationFrame(() => {
      setIsVisible(true);
      openFrameRef.current = null;
    });

    return () => {
      if (openFrameRef.current) {
        cancelAnimationFrame(openFrameRef.current);
        openFrameRef.current = null;
      }
    };
  }, [expanded, isRendered, isVisible]);

  useEffect(() => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }

    if (!allowOverflowWhenOpen || !expanded || !isVisible || isSettled) {
      return undefined;
    }

    const delay = prefersReducedMotion() ? 0 : DISCLOSURE_MOTION_MS;
    settleTimerRef.current = setTimeout(() => {
      setIsSettled(true);
      settleTimerRef.current = null;
    }, delay);

    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };
  }, [allowOverflowWhenOpen, expanded, isSettled, isVisible]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (openFrameRef.current) cancelAnimationFrame(openFrameRef.current);
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    },
    [],
  );

  if (!isRendered) return null;

  return (
    <div
      className={`sx-disclosure-motion${
        isVisible ? " sx-disclosure-motion--open" : ""
      }${
        allowOverflowWhenOpen && expanded && isSettled
          ? " sx-disclosure-motion--overflow-visible"
          : ""
      }`}
      aria-hidden={!expanded}
      inert={!expanded ? "" : undefined}
      data-testid={testId}
    >
      <div className="sx-disclosure-motion__clip">
        <div
          className={contentClassName}
          role={role}
          aria-label={ariaLabel}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
